import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import type { SentimentComplaintRow } from "@/lib/sentiment-types";

interface ComplaintTableProps {
  complaints: SentimentComplaintRow[];
  onTicketClick?: (ticketId: string) => void;
}

const severityColor = (severity: number): string => {
  if (severity >= 5) return "bg-red-600 text-white";
  if (severity >= 4) return "bg-red-500 text-white";
  if (severity >= 3) return "bg-orange-500 text-white";
  if (severity >= 2) return "bg-yellow-500 text-white";
  return "bg-green-500 text-white";
};

const categoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    delay: "bg-blue-100 text-blue-800",
    error: "bg-red-100 text-red-800",
    negligence: "bg-purple-100 text-purple-800",
    poor_communication: "bg-orange-100 text-orange-800",
    quality_issue: "bg-pink-100 text-pink-800",
    broken_promise: "bg-red-100 text-red-700",
    ignored_request: "bg-yellow-100 text-yellow-800",
    missing_information: "bg-indigo-100 text-indigo-800",
    unprofessional: "bg-gray-100 text-gray-800",
    other: "bg-gray-100 text-gray-600",
  };
  return colors[category] || "bg-gray-100 text-gray-600";
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  } catch {
    return "-";
  }
};

export function ComplaintTable({ complaints, onTicketClick }: ComplaintTableProps) {
  if (complaints.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No complaints found matching your filters.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[60px]">Severity</TableHead>
          <TableHead>Ticket</TableHead>
          <TableHead>Title Ref</TableHead>
          <TableHead>From</TableHead>
          <TableHead>Categories</TableHead>
          <TableHead>Summary</TableHead>
          <TableHead>Date</TableHead>
          <TableHead className="w-[40px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {complaints.map((c) => (
          <TableRow
            key={c.emailId}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => onTicketClick?.(c.ticketId)}
          >
            <TableCell>
              <Badge className={severityColor(c.severity)}>{c.severity}/5</Badge>
            </TableCell>
            <TableCell className="max-w-[200px] truncate font-medium">
              {c.ticketSubject || c.ticketId}
            </TableCell>
            <TableCell className="text-sm text-gray-600">{c.titleReference || "-"}</TableCell>
            <TableCell className="text-sm">
              {c.senderName || c.senderEmail || "-"}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {c.categories.map((cat) => (
                  <Badge key={cat} variant="outline" className={`text-xs ${categoryColor(cat)}`}>
                    {cat.replace("_", " ")}
                  </Badge>
                ))}
              </div>
            </TableCell>
            <TableCell className="max-w-[300px] truncate text-sm text-gray-700">
              {c.summary || "-"}
            </TableCell>
            <TableCell className="text-sm text-gray-500">
              {formatDate(c.engagementTimestamp)}
            </TableCell>
            <TableCell>
              {c.hubSpotUrl && (
                <a
                  href={c.hubSpotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                </a>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
